import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ImageOff } from 'lucide-react';
import { ZipCodeValidator } from '../components/ZipCodeValidator';
import { DeliveryZoneInfo } from '../components/DeliveryZoneInfo';
import { BookingForm } from '../components/BookingForm';
import { BookingThankYou } from '../components/BookingThankYou';
import { ImageWithFallback } from '../components/ImageWithFallback';

export function Booking() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLocationValid, setIsLocationValid] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingDetails, setBookingDetails] = useState<{
    orderNumber: string;
    customerName: string;
  } | null>(null);
  
  const product = location.state?.product;

  if (!product) {
    navigate('/inventory');
    return null;
  }

  // Get the first valid image URL from the product's images array
  const productImage = product.images && product.images.length > 0 
    ? product.images[0].url 
    : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {!bookingComplete && (
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Product
            </button>
          )}

          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6">
              {bookingComplete && bookingDetails ? (
                <BookingThankYou {...bookingDetails} />
              ) : (
                <>
                  <h1 className="text-2xl font-bold text-gray-900 mb-6">Schedule Installation</h1>

                  <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                    <h2 className="font-semibold text-blue-900">Selected Product</h2>
                    <div className="mt-2 flex items-center">
                      <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                        <ImageWithFallback
                          src={productImage}
                          alt={product.name}
                          className="w-full h-full object-contain"
                          fallbackText={product.name}
                        />
                      </div>
                      <div className="ml-4">
                        <p className="font-medium text-gray-900">{product.name}</p>
                        <p className="text-gray-600">${product.price.toFixed(2)}</p>
                        <p className="text-sm text-gray-500 mt-1">{product.brand}</p>
                      </div>
                    </div>
                  </div>

                  {/* Step 1: Check Delivery Zone */}
                  <div className="mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">
                      Step 1: Check Delivery Zone
                    </h2>
                    <ZipCodeValidator onValidation={setIsLocationValid} />
                  </div>

                  {/* Step 2: Booking Form (only shown if location is valid) */}
                  {isLocationValid ? (
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        Step 2: Schedule Your Installation
                      </h2>
                      <BookingForm
                        product={product}
                        onBookingComplete={(details) => {
                          setBookingDetails(details);
                          setBookingComplete(true);
                        }}
                      />
                    </div>
                  ) : (
                    <DeliveryZoneInfo className="mt-8" />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}